import { Lightbulb } from "lucide-react";
import type { KeyboardEvent } from "react";

import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { cn } from "@krea/shared/utils";

import type {
  BidTypeEnum,
  OfferAttractivenessRecommendationStatusEnum,
} from "@/features/common/enums";
import type { IOfferAttractivenessRecommendation } from "@/features/lender-responses/types/offer-attractiveness";

import {
  getAlertVariant,
  getAttractivenessColor,
  getQualityTitle,
  getRecommendationDisplayText,
  getRecommendationIcon,
} from "./utils";

interface OfferAttractivenessCardProps {
  score: number | null;
  recommendations: IOfferAttractivenessRecommendation[];
  bidType: BidTypeEnum | undefined;
  onRecommendationClick?: (
    status: OfferAttractivenessRecommendationStatusEnum,
  ) => void;
  recommendationsClickable?: boolean;
  className?: string;
}

const OfferAttractivenessCard = ({
  score,
  recommendations,
  bidType,
  onRecommendationClick,
  recommendationsClickable = true,
  className,
}: OfferAttractivenessCardProps) => {
  const isEmpty = score === null;
  const clampedScore = score === null ? null : Math.min(Math.max(score, 0), 10);
  const numericScore = clampedScore;
  const indicatorPosition =
    clampedScore !== null ? (clampedScore / 10) * 100 : 0;
  const segments = Array.from({ length: 10 });
  const ticks = Array.from({ length: 11 }, (_, i) => i);

  return (
    <div className={cn("relative space-y-4", className)}>
      <div className="pb-4">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center text-section">
            Offer Attractiveness
          </h2>
        </div>
        <p className="text-base">
          Estimated attractiveness score for converting the customer based on
          current offer.
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <div className="relative mb-2 h-4 w-full whitespace-nowrap px-2 text-[10px] font-semibold uppercase tracking-wider">
            <span className="absolute left-[5%] -translate-x-1/2 text-red">
              Low
            </span>
            <span className="absolute left-1/4 -translate-x-1/2 text-orange-500">
              Medium
            </span>
            <span className="absolute left-[60%] -translate-x-1/2 text-mint-400">
              High
            </span>
            <span className="absolute left-[90%] -translate-x-1/2 text-mint-500">
              Very high
            </span>
          </div>

          <div className="relative mt-4 px-2">
            {!isEmpty && clampedScore !== null && (
              <div className="absolute left-2 right-2 top-[-14px] z-20">
                {/* typography: score indicator chip — sm/semibold/white is its own decorative contract */}
                <div
                  className={cn(
                    "absolute flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-sm font-semibold text-white shadow-lg transition-transform duration-500 ease-out",
                    getAttractivenessColor(clampedScore, "number"),
                    `translate-x-[-50%]`,
                  )}
                  style={{ left: `${indicatorPosition}%` }}
                >
                  {clampedScore}
                </div>
              </div>
            )}

            <div className="flex h-4 w-full overflow-hidden rounded-full">
              {segments.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex-1 border-r border-white/20 last:border-r-0",
                    getAttractivenessColor(i, "gauge"),
                    i >= (clampedScore ?? 0) ? "opacity-30" : "opacity-100",
                  )}
                />
              ))}
            </div>

            <div className="relative mt-2 h-6 w-full">
              {ticks.map((num) => {
                const positionPercent = (num / 10) * 100;
                const isActive = !isEmpty && num <= (clampedScore ?? -1);

                return (
                  <div
                    key={num}
                    className="absolute flex -translate-x-1/2 flex-col items-center"
                    style={{ left: `${positionPercent}%` }}
                  >
                    <div className="mb-1 h-1 w-px bg-gray-300" />
                    {/* typography: dense 10px tick label — active/inactive states define their own visual contract, no preset matches */}
                    <span
                      className={cn(
                        "text-[10px]",
                        isActive
                          ? "font-semibold text-gray-600"
                          : "font-medium text-gray-300",
                      )}
                    >
                      {num}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {isEmpty ? (
            <Alert
              variant="info"
              leftElement={
                // typography: score indicator chip — sm/semibold is its own decorative contract
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-white text-sm font-semibold text-primary shadow-lg">
                  ?
                </div>
              }
            >
              <Alert.Description>
                Please fill in an offer to estimate the attractiveness
              </Alert.Description>
            </Alert>
          ) : (
            <Alert
              leftElement={
                numericScore !== null && (
                  // typography: score indicator chip — sm/semibold/white is its own decorative contract
                  <div
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-white text-sm font-semibold text-white shadow-lg",
                      getAttractivenessColor(numericScore, "number"),
                    )}
                  >
                    {numericScore}
                  </div>
                )
              }
              variant={getAlertVariant(score)}
            >
              <Alert.Title>
                {numericScore !== null && getQualityTitle(numericScore)}
              </Alert.Title>
              {numericScore !== null && numericScore < 5 && (
                <Alert.Description>
                  Consider adjusting terms to match top offers.
                </Alert.Description>
              )}
            </Alert>
          )}
        </div>

        {recommendations.length > 0 && (
          <div>
            <h3 className="mb-3 flex items-center text-base-strong">
              <Lightbulb className="mr-2 size-4 text-primary" />
              Recommendations
            </h3>
            <div className="space-y-3">
              {recommendations.map((rec) => (
                <div
                  key={rec.statusText}
                  className={cn(
                    "group rounded-lg border border-gray-200 bg-white p-3",
                    recommendationsClickable &&
                      "cursor-pointer hover:border-primary-300",
                  )}
                  {...(recommendationsClickable && {
                    onClick: () => onRecommendationClick?.(rec.status),
                    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRecommendationClick?.(rec.status);
                      }
                    },
                    role: "button",
                    tabIndex: 0,
                  })}
                >
                  <div className="flex items-center">
                    <div
                      className={cn(
                        "mr-3 rounded-sm bg-primary-150 p-1.5 align-middle",
                        recommendationsClickable &&
                          "group-hover:bg-primary-100",
                      )}
                    >
                      {getRecommendationIcon(rec.status)}
                    </div>
                    <p className="mb-0 text-base-strong">
                      {getRecommendationDisplayText(
                        rec.status,
                        bidType,
                        rec.statusText,
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { OfferAttractivenessCard };
